// src/utils/analytics.js
import ReactGA from 'react-ga4';

export const initGA = () => {
  ReactGA.initialize('G-6E24KPMK4B', {
    debug: process.env.NODE_ENV === 'development',
    titleCase: false,
    gaOptions: {
      custom_map: {
        custom_parameter_1: 'wedding_type',
        custom_parameter_2: 'cultural_background',
        custom_parameter_3: 'location_type',
        custom_parameter_4: 'package_type',
        custom_parameter_5: 'storytelling_style'
      }
    }
  });
};

// Enhanced booking tracking
export const trackBookNowClick = (label = 'Custom Booking', weddingType = '', culturalBackground = '') => {
  ReactGA.event({
    category: 'Booking',
    action: 'Clicked Book Now',
    label: label,
    custom_parameter_1: weddingType,
    custom_parameter_2: culturalBackground
  });
};

// Storytelling engagement tracking
export const trackStorytellingEngagement = (action, label = '', value = 0) => {
  ReactGA.event({
    category: 'Storytelling Engagement',
    action: action,
    label: label,
    value: value,
    custom_parameter_5: 'narrative_photography'
  });
};

// Indigenous wedding specific tracking
export const trackIndigenousWeddingInterest = (indigenousGroup, action, label = '') => {
  ReactGA.event({
    category: 'Indigenous Wedding Interest',
    action: action,
    label: label,
    custom_parameter_2: indigenousGroup,
    custom_parameter_5: 'cultural_storytelling'
  });
};

// Package selection tracking
export const trackPackageSelection = (packageName, packageType, price = 0) => {
  ReactGA.event({
    category: 'Package Selection',
    action: 'Selected Package',
    label: packageName,
    value: price,
    custom_parameter_4: packageType
  });
};

// Location-based tracking with main focus areas
export const trackLocationInterest = (location, locationType, action = 'Viewed') => {
  // Main locations get higher priority tracking
  const mainLocations = ['Chittagong', 'Bandarban', 'Rangamati', 'Khagrachari', 'Cox\'s Bazar'];
  const isMainLocation = mainLocations.includes(location);
  
  ReactGA.event({
    category: 'Location Interest',
    action: action,
    label: location,
    custom_parameter_3: locationType,
    value: isMainLocation ? 10 : 5 // Higher value for main locations
  });
};

// International coverage tracking
export const trackInternationalInterest = (country, action = 'Viewed') => {
  ReactGA.event({
    category: 'International Interest',
    action: action,
    label: country,
    custom_parameter_3: 'international',
    custom_parameter_5: 'global_storytelling'
  });
};

// Bangladesh coverage tracking
export const trackBangladeshCoverage = (division, action = 'Viewed') => {
  ReactGA.event({
    category: 'Bangladesh Coverage',
    action: action,
    label: division,
    custom_parameter_3: 'bangladesh',
    custom_parameter_5: 'national_storytelling'
  });
};

// Portfolio engagement tracking
export const trackPortfolioEngagement = (action, coupleNames = '', culturalType = '') => {
  ReactGA.event({
    category: 'Portfolio Engagement',
    action: action,
    label: coupleNames,
    custom_parameter_2: culturalType,
    custom_parameter_5: 'visual_storytelling'
  });
};

// Film/video engagement tracking
export const trackFilmEngagement = (action, filmTitle = '', culturalType = '') => {
  ReactGA.event({
    category: 'Film Engagement',
    action: action,
    label: filmTitle,
    custom_parameter_2: culturalType,
    custom_parameter_5: 'cinematic_storytelling'
  });
};

// Contact form tracking
export const trackContactFormEngagement = (action, inquiryType = '', culturalBackground = '') => {
  ReactGA.event({
    category: 'Contact Form',
    action: action,
    label: inquiryType,
    custom_parameter_2: culturalBackground
  });
};

// Budget calculator tracking
export const trackBudgetCalculator = (action, estimatedBudget = 0, weddingType = '') => {
  ReactGA.event({
    category: 'Budget Calculator',
    action: action,
    label: `Budget: ${estimatedBudget}`,
    value: estimatedBudget,
    custom_parameter_1: weddingType
  });
};

// Social media tracking
export const trackSocialMediaClick = (platform, action = 'Clicked') => {
  ReactGA.event({
    category: 'Social Media',
    action: action,
    label: platform
  });
};

// WhatsApp tracking
export const trackWhatsAppClick = (context = 'General Inquiry', culturalBackground = '') => {
  ReactGA.event({
    category: 'WhatsApp',
    action: 'Clicked WhatsApp',
    label: context,
    custom_parameter_2: culturalBackground
  });
};

// Conversion tracking
export const trackBooking = (bookingData) => {
  ReactGA.event({
    category: 'Conversion',
    action: 'Booking Completed',
    label: bookingData.packageName || 'Custom Package',
    value: bookingData.totalCost || 0,
    custom_parameter_1: bookingData.eventType || '',
    custom_parameter_2: bookingData.culturalBackground || '',
    custom_parameter_3: bookingData.location || '',
    custom_parameter_4: bookingData.packageType || '',
    custom_parameter_5: 'storytelling_booking'
  });
};

// Enhanced ecommerce tracking
export const trackPurchase = (transactionId, items, totalValue) => {
  ReactGA.gtag('event', 'purchase', {
    transaction_id: transactionId,
    value: totalValue,
    currency: 'BDT',
    items: items.map(item => ({
      item_id: item.id,
      item_name: item.name,
      category: item.category,
      quantity: item.quantity,
      price: item.price
    }))
  });
};

// Page view tracking with custom parameters
export const trackPageView = (pageName, pageType = '', culturalFocus = '') => {
  ReactGA.send({
    hitType: 'pageview',
    page: window.location.pathname,
    title: pageName,
    custom_parameter_5: culturalFocus || 'storytelling'
  });
};

// Search tracking
export const trackSearch = (searchTerm, searchCategory = '') => {
  ReactGA.event({
    category: 'Search',
    action: 'Searched',
    label: searchTerm,
    custom_parameter_4: searchCategory
  });
};

// Newsletter/email signup tracking
export const trackEmailSignup = (source = '', culturalInterest = '') => {
  ReactGA.event({
    category: 'Email Signup',
    action: 'Subscribed',
    label: source,
    custom_parameter_2: culturalInterest
  });
};

// Error tracking
export const trackError = (errorType, errorMessage = '', page = '') => {
  ReactGA.event({
    category: 'Error',
    action: errorType,
    label: `${page}: ${errorMessage}`,
    non_interaction: true
  });
};

// Performance tracking
export const trackPerformance = (metric, value, page = '') => {
  ReactGA.event({
    category: 'Performance',
    action: metric,
    label: page,
    value: Math.round(value),
    non_interaction: true
  });
};

// Competitive advantage tracking
export const trackCompetitiveAdvantage = (advantage, action = 'Viewed') => {
  ReactGA.event({
    category: 'Competitive Advantage',
    action: action,
    label: advantage,
    custom_parameter_5: 'competitive_positioning'
  });
};

// Market leadership tracking
export const trackMarketLeadership = (metric, value = 0) => {
  ReactGA.event({
    category: 'Market Leadership',
    action: 'Leadership Metric',
    label: metric,
    value: value,
    custom_parameter_5: 'market_dominance'
  });
};

// Client satisfaction tracking
export const trackClientSatisfaction = (satisfactionLevel, source = '') => {
  ReactGA.event({
    category: 'Client Satisfaction',
    action: 'Satisfaction Level',
    label: satisfactionLevel,
    custom_parameter_2: source,
    custom_parameter_5: 'quality_assurance'
  });
};

// Award and recognition tracking
export const trackAwardRecognition = (award, year = '') => {
  ReactGA.event({
    category: 'Award Recognition',
    action: 'Award Received',
    label: award,
    custom_parameter_2: year,
    custom_parameter_5: 'industry_recognition'
  });
};

// Experience and expertise tracking
export const trackExperienceExpertise = (expertise, years = 0) => {
  ReactGA.event({
    category: 'Experience Expertise',
    action: 'Expertise Demonstrated',
    label: expertise,
    value: years,
    custom_parameter_5: 'professional_expertise'
  });
};

// Quality and excellence tracking
export const trackQualityExcellence = (qualityMetric, score = 0) => {
  ReactGA.event({
    category: 'Quality Excellence',
    action: 'Quality Metric',
    label: qualityMetric,
    value: score,
    custom_parameter_5: 'excellence_standard'
  });
};

// Innovation and creativity tracking
export const trackInnovationCreativity = (innovation, creativity = '') => {
  ReactGA.event({
    category: 'Innovation Creativity',
    action: 'Innovation Demonstrated',
    label: innovation,
    custom_parameter_2: creativity,
    custom_parameter_5: 'creative_excellence'
  });
};

// Trust and reliability tracking
export const trackTrustReliability = (trustFactor, reliability = '') => {
  ReactGA.event({
    category: 'Trust Reliability',
    action: 'Trust Factor',
    label: trustFactor,
    custom_parameter_2: reliability,
    custom_parameter_5: 'trust_building'
  });
};

// Competitive comparison tracking
export const trackCompetitiveComparison = (comparison, outcome = '') => {
  ReactGA.event({
    category: 'Competitive Comparison',
    action: 'Comparison Made',
    label: comparison,
    custom_parameter_2: outcome,
    custom_parameter_5: 'competitive_analysis'
  });
};

// Industrial-grade market domination tracking
export const trackMarketDomination = (metric, dominance = 0) => {
  ReactGA.event({
    category: 'Market Domination',
    action: 'Domination Metric',
    label: metric,
    value: dominance,
    custom_parameter_5: 'market_supremacy'
  });
};

// Industry leadership tracking
export const trackIndustryLeadership = (leadership, industry = '') => {
  ReactGA.event({
    category: 'Industry Leadership',
    action: 'Leadership Demonstrated',
    label: leadership,
    custom_parameter_2: industry,
    custom_parameter_5: 'industry_supremacy'
  });
};

// Unparalleled expertise tracking
export const trackUnparalleledExpertise = (expertise, uniqueness = '') => {
  ReactGA.event({
    category: 'Unparalleled Expertise',
    action: 'Expertise Demonstrated',
    label: expertise,
    custom_parameter_2: uniqueness,
    custom_parameter_5: 'expertise_supremacy'
  });
};

// Peerless quality tracking
export const trackPeerlessQuality = (quality, standard = '') => {
  ReactGA.event({
    category: 'Peerless Quality',
    action: 'Quality Demonstrated',
    label: quality,
    custom_parameter_2: standard,
    custom_parameter_5: 'quality_supremacy'
  });
};

// Revolutionary innovation tracking
export const trackRevolutionaryInnovation = (innovation, breakthrough = '') => {
  ReactGA.event({
    category: 'Revolutionary Innovation',
    action: 'Innovation Demonstrated',
    label: innovation,
    custom_parameter_2: breakthrough,
    custom_parameter_5: 'innovation_supremacy'
  });
};

// Groundbreaking achievement tracking
export const trackGroundbreakingAchievement = (achievement, impact = '') => {
  ReactGA.event({
    category: 'Groundbreaking Achievement',
    action: 'Achievement Demonstrated',
    label: achievement,
    custom_parameter_2: impact,
    custom_parameter_5: 'achievement_supremacy'
  });
};

// Visionary leadership tracking
export const trackVisionaryLeadership = (vision, future = '') => {
  ReactGA.event({
    category: 'Visionary Leadership',
    action: 'Vision Demonstrated',
    label: vision,
    custom_parameter_2: future,
    custom_parameter_5: 'vision_supremacy'
  });
};

// Trailblazer excellence tracking
export const trackTrailblazerExcellence = (excellence, pioneering = '') => {
  ReactGA.event({
    category: 'Trailblazer Excellence',
    action: 'Excellence Demonstrated',
    label: excellence,
    custom_parameter_2: pioneering,
    custom_parameter_5: 'excellence_supremacy'
  });
};

// Pioneer mastery tracking
export const trackPioneerMastery = (mastery, innovation = '') => {
  ReactGA.event({
    category: 'Pioneer Mastery',
    action: 'Mastery Demonstrated',
    label: mastery,
    custom_parameter_2: innovation,
    custom_parameter_5: 'mastery_supremacy'
  });
};

// Innovator supremacy tracking
export const trackInnovatorSupremacy = (supremacy, innovation = '') => {
  ReactGA.event({
    category: 'Innovator Supremacy',
    action: 'Supremacy Demonstrated',
    label: supremacy,
    custom_parameter_2: innovation,
    custom_parameter_5: 'supremacy_demonstration'
  });
};

// Supreme service tracking
export const trackSupremeService = (service, excellence = '') => {
  ReactGA.event({
    category: 'Supreme Service',
    action: 'Service Demonstrated',
    label: service,
    custom_parameter_2: excellence,
    custom_parameter_5: 'service_supremacy'
  });
};

// Ultimate solution tracking
export const trackUltimateSolution = (solution, perfection = '') => {
  ReactGA.event({
    category: 'Ultimate Solution',
    action: 'Solution Demonstrated',
    label: solution,
    custom_parameter_2: perfection,
    custom_parameter_5: 'solution_supremacy'
  });
};

// Perfect package tracking
export const trackPerfectPackage = (packageType, ideal = '') => {
  ReactGA.event({
    category: 'Perfect Package',
    action: 'Package Demonstrated',
    label: packageType,
    custom_parameter_2: ideal,
    custom_parameter_5: 'package_supremacy'
  });
};

// Ideal service tracking
export const trackIdealService = (service, perfection = '') => {
  ReactGA.event({
    category: 'Ideal Service',
    action: 'Service Demonstrated',
    label: service,
    custom_parameter_2: perfection,
    custom_parameter_5: 'ideal_supremacy'
  });
};

// Optimal solution tracking
export const trackOptimalSolution = (solution, maximum = '') => {
  ReactGA.event({
    category: 'Optimal Solution',
    action: 'Solution Demonstrated',
    label: solution,
    custom_parameter_2: maximum,
    custom_parameter_5: 'optimal_supremacy'
  });
};

// Maximum package tracking
export const trackMaximumPackage = (packageType, peak = '') => {
  ReactGA.event({
    category: 'Maximum Package',
    action: 'Package Demonstrated',
    label: packageType,
    custom_parameter_2: peak,
    custom_parameter_5: 'maximum_supremacy'
  });
};

// Peak service tracking
export const trackPeakService = (service, summit = '') => {
  ReactGA.event({
    category: 'Peak Service',
    action: 'Service Demonstrated',
    label: service,
    custom_parameter_2: summit,
    custom_parameter_5: 'peak_supremacy'
  });
};

// Summit solution tracking
export const trackSummitSolution = (solution, zenith = '') => {
  ReactGA.event({
    category: 'Summit Solution',
    action: 'Solution Demonstrated',
    label: solution,
    custom_parameter_2: zenith,
    custom_parameter_5: 'summit_supremacy'
  });
};

// Zenith package tracking
export const trackZenithPackage = (packageType, pinnacle = '') => {
  ReactGA.event({
    category: 'Zenith Package',
    action: 'Package Demonstrated',
    label: packageType,
    custom_parameter_2: pinnacle,
    custom_parameter_5: 'zenith_supremacy'
  });
};

// Pinnacle service tracking
export const trackPinnacleService = (service, apex = '') => {
  ReactGA.event({
    category: 'Pinnacle Service',
    action: 'Service Demonstrated',
    label: service,
    custom_parameter_2: apex,
    custom_parameter_5: 'pinnacle_supremacy'
  });
};

// Apex solution tracking
export const trackApexSolution = (solution, culmination = '') => {
  ReactGA.event({
    category: 'Apex Solution',
    action: 'Solution Demonstrated',
    label: solution,
    custom_parameter_2: culmination,
    custom_parameter_5: 'apex_supremacy'
  });
};

// Culmination package tracking
export const trackCulminationPackage = (packageType, climax = '') => {
  ReactGA.event({
    category: 'Culmination Package',
    action: 'Package Demonstrated',
    label: packageType,
    custom_parameter_2: climax,
    custom_parameter_5: 'culmination_supremacy'
  });
};

// Climax service tracking
export const trackClimaxService = (service, crescendo = '') => {
  ReactGA.event({
    category: 'Climax Service',
    action: 'Service Demonstrated',
    label: service,
    custom_parameter_2: crescendo,
    custom_parameter_5: 'climax_supremacy'
  });
};

// Crescendo solution tracking
export const trackCrescendoSolution = (solution, finale = '') => {
  ReactGA.event({
    category: 'Crescendo Solution',
    action: 'Solution Demonstrated',
    label: solution,
    custom_parameter_2: finale,
    custom_parameter_5: 'crescendo_supremacy'
  });
};

// Grand finale tracking
export const trackGrandFinale = (finale, magnum = '') => {
  ReactGA.event({
    category: 'Grand Finale',
    action: 'Finale Demonstrated',
    label: finale,
    custom_parameter_2: magnum,
    custom_parameter_5: 'finale_supremacy'
  });
};

// Magnum opus tracking
export const trackMagnumOpus = (opus, masterpiece = '') => {
  ReactGA.event({
    category: 'Magnum Opus',
    action: 'Opus Demonstrated',
    label: opus,
    custom_parameter_2: masterpiece,
    custom_parameter_5: 'opus_supremacy'
  });
};

// Masterpiece tracking
export const trackMasterpiece = (masterpiece, tour = '') => {
  ReactGA.event({
    category: 'Masterpiece',
    action: 'Masterpiece Demonstrated',
    label: masterpiece,
    custom_parameter_2: tour,
    custom_parameter_5: 'masterpiece_supremacy'
  });
};

// Tour de force tracking
export const trackTourDeForce = (force, coup = '') => {
  ReactGA.event({
    category: 'Tour de Force',
    action: 'Force Demonstrated',
    label: force,
    custom_parameter_2: coup,
    custom_parameter_5: 'force_supremacy'
  });
};

// Coup de grace tracking
export const trackCoupDeGrace = (grace, piece = '') => {
  ReactGA.event({
    category: 'Coup de Grace',
    action: 'Grace Demonstrated',
    label: grace,
    custom_parameter_2: piece,
    custom_parameter_5: 'grace_supremacy'
  });
};

// Piece de resistance tracking
export const trackPieceDeResistance = (resistance, supremacy = '') => {
  ReactGA.event({
    category: 'Piece de Resistance',
    action: 'Resistance Demonstrated',
    label: resistance,
    custom_parameter_2: supremacy,
    custom_parameter_5: 'resistance_supremacy'
  });
};